import { Vector3 } from 'three'
import { PlanLabelPdfMetrics } from '@editor/hooks/usePlanLabelPdfMetrics'
import type { StiffeningSegmentPlanAxis } from '@editor/utils'
import { getCenterMultipleReduceMean, ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  getPositionLabelPlacement,
  LabelPlacement,
} from 'src/components/scene/ModelElementLabels/utils'
import {
  BEAM_LABEL_Z_OFFSET,
  DEFAULT_COLUMN_CROSS_SECTION_SIZE,
  EXPORT_SEGMENT_WALL_THICKNESS_WORLD,
  FALLBACK_WALL_THICKNESS,
  FLOORPLAN_LABEL_Z_OFFSET,
  PLAN_LABEL_GAP_WORLD,
  POSITION_LABEL_Z_LIFT,
  SHEAR_ANCHOR_LABEL_MARGIN_PT,
  STIFFENING_PLAN_ANCHOR_LABEL_Z_LIFT,
  TENSILE_ANCHOR_LABEL_DISTANCE_WORLD,
} from './constants'
import {
  getPdfLabelBoxHeightWorld,
  getPdfLabelBoxSizeWorld,
  getPdfTextBlockSizeWorld,
} from './pdfLabelBox'

/** Same fallback the rip/lintel meshes use for a missing cross section */
export const DEFAULT_CROSS_SECTION_SIZE = 0.15

/**
 * Default (non-moved) world placements of the movable floorplan labels,
 * mirroring where the plan PDF export puts them. The descriptor hooks and
 * `PlanLabelController` both use these, so the HUD and the drag origin sit
 * exactly where the label is drawn.
 */

/**
 * The export (`planarWallV2.planLabelPositionWorld`) anchors the wall label at
 * the wall footprint edge — axis center plus the plan normal (z × direction)
 * times half the structural thickness — with the label box's near edge on that
 * point: bottom edge for horizontal labels, right edge for vertical ones
 * (which read bottom-up like the PDF's 90° rotation). Our labels are
 * center-anchored, so half the PDF box height is added on top.
 */
export const getWallLabelPlacement = (
  wall: ShapeObject,
  structuralThickness: number | undefined,
  metrics: PlanLabelPdfMetrics,
): LabelPlacement | null => {
  const [p0, p1] = wall.shape.points
  if (!p0 || !p1) return null

  const direction = p1.sub(p0)
  const length = Math.hypot(direction.x, direction.y)
  if (!length) return null

  const center = p0.add(p1).divideScalar(2)
  // Matches `planLabelOrientation`: exactly diagonal walls read horizontally.
  const isVertical = Math.abs(direction.x) < Math.abs(direction.y)
  const normal = new ImmutableVector3(-direction.y / length, direction.x / length, 0)

  // `||`, not `??`: matches `RealThicknessWallsMesh`'s fallback, which also
  // treats a resolved thickness of exactly 0 as "no assembly".
  const thickness = structuralThickness || FALLBACK_WALL_THICKNESS
  const edge = center.add(normal.multiplyScalar(thickness / 2))

  // Wall labels are borderless in the PDF. The anchor shift runs along the
  // page axes (not the wall normal), exactly like `LabelAnchorLocations`.
  const halfBox = getPdfLabelBoxHeightWorld(metrics, false) / 2
  const position = new ImmutableVector3(
    edge.x + (isVertical ? -halfBox : 0),
    edge.y + (isVertical ? 0 : halfBox),
    center.z + FLOORPLAN_LABEL_Z_OFFSET,
  )

  return {
    position,
    xDirection: isVertical ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0),
    yDirection: isVertical ? new Vector3(-1, 0, 0) : new Vector3(0, 1, 0),
  }
}

/**
 * Reading directions of the stiffening plan's segment-bound labels: horizontal only on exactly
 * horizontal walls, bottom-up (the PDF's 90° rotation) on every other one.
 */
const getStiffeningSegmentLabelDirections = (
  axis: StiffeningSegmentPlanAxis,
): Pick<LabelPlacement, 'xDirection' | 'yDirection'> => ({
  xDirection: axis.isHorizontal ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0),
  yDirection: axis.isHorizontal ? new Vector3(0, 1, 0) : new Vector3(-1, 0, 0),
})

/**
 * The export (`StiffeningSegmentV2`) centers the segment label on the segment (anchor
 * `Center`, not movable), horizontal only on exactly horizontal walls.
 *
 * The label lies on top of the segment footprint, which (like the wall footprints) is lifted to
 * the storey top, so the label is lifted there too — at the wall's own height the footprint
 * would hide it in the depth test.
 */
export const getStiffeningSegmentLabelPlacement = (
  axis: StiffeningSegmentPlanAxis,
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number,
): LabelPlacement => {
  const center = axis.start.add(axis.end).divideScalar(2)
  const z = Math.max(axis.start.z, axis.end.z, storeyTopZ ?? -Infinity) + FLOORPLAN_LABEL_Z_OFFSET
  return {
    position: new ImmutableVector3(center.x, center.y, z),
    ...getStiffeningSegmentLabelDirections(axis),
  }
}

/** The anchor labels are written after the segment labels, so they stack on top of them. */
const getStiffeningAnchorLabelZ = (axis: StiffeningSegmentPlanAxis, storeyTopZ?: number) =>
  Math.max(axis.start.z, axis.end.z, storeyTopZ ?? -Infinity) +
  FLOORPLAN_LABEL_Z_OFFSET +
  STIFFENING_PLAN_ANCHOR_LABEL_Z_LIFT

/**
 * The stiffening plan export (`BluebeamPDFPlanWriter.stiffening_plan`) centers a Zuganker label
 * `ANCHOR_PLOT_DISTANCE` inside the segment from the segment end it anchors, oriented like the
 * segment label.
 */
export const getTensileAnchorLabelPlacement = (
  axis: StiffeningSegmentPlanAxis,
  segmentEnd: 'start' | 'end',
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number,
): LabelPlacement => {
  const inward = (segmentEnd === 'start' ? axis.end.sub(axis.start) : axis.start.sub(axis.end))
    .normalize()
    .multiplyScalar(TENSILE_ANCHOR_LABEL_DISTANCE_WORLD)
  const position = (segmentEnd === 'start' ? axis.start : axis.end).add(inward)
  return {
    position: new ImmutableVector3(
      position.x,
      position.y,
      getStiffeningAnchorLabelZ(axis, storeyTopZ),
    ),
    ...getStiffeningSegmentLabelDirections(axis),
  }
}

/**
 * The export's `_shear_anchor_label_position`: the Schubanker label goes beside the segment's
 * center — below a horizontal wall, right of any other one — just clear of the drawn wall. The
 * distance is half the export segment footprint (`WALL_THICKNESS_WORLD`), plus half the label
 * box, plus `SHEAR_ANCHOR_LABEL_MARGIN_POINTS` on paper.
 *
 * The export's "half the label box" (`_label_half_extent_points`) is the box the annotation maker
 * draws (`getPdfLabelBoxSizeWorld`), measured along the direction the label moves, so its nearest
 * corner keeps the margin to the wall whatever the wall's direction. A vertical label is the same
 * box turned by 90 degrees. That is mirrored exactly, so the preview puts the label where the PDF
 * does.
 */
export const getShearAnchorLabelPlacement = (
  axis: StiffeningSegmentPlanAxis,
  text: string,
  metrics: PlanLabelPdfMetrics,
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number,
): LabelPlacement => {
  const along = axis.end.sub(axis.start)
  // perpendicular to the segment, pointing right (+x); straight down for horizontal walls
  const perpendicular = new ImmutableVector3(-along.y, along.x, 0).normalize()
  const direction = axis.isHorizontal
    ? new ImmutableVector3(0, -1, 0)
    : perpendicular.x > 0
    ? perpendicular
    : perpendicular.multiplyScalar(-1)

  const box = getPdfLabelBoxSizeWorld([text], metrics)
  const [boxXExtent, boxYExtent] = axis.isHorizontal
    ? [box.width, box.height]
    : [box.height, box.width]
  const halfLabelExtent =
    (Math.abs(direction.x) * boxXExtent + Math.abs(direction.y) * boxYExtent) / 2
  const distance =
    EXPORT_SEGMENT_WALL_THICKNESS_WORLD / 2 +
    halfLabelExtent +
    SHEAR_ANCHOR_LABEL_MARGIN_PT * metrics.ptToWorld

  const center = axis.start.add(axis.end).divideScalar(2).add(direction.multiplyScalar(distance))
  return {
    position: new ImmutableVector3(center.x, center.y, getStiffeningAnchorLabelZ(axis, storeyTopZ)),
    ...getStiffeningSegmentLabelDirections(axis),
  }
}

export const getBeamLabelCenter = (beam: ShapeObjectLine): ImmutableVector3 =>
  beam.shape.start
    .add(beam.shape.end)
    .divideScalar(2)
    .add(new ImmutableVector3(0, 0, BEAM_LABEL_Z_OFFSET))

/**
 * Purlins lie beneath the roof slabs of their storey, so the label is lifted to the storey top -
 * at the purlin's own height the roof above it would catch the click meant for the label.
 */
export const getPurlinLabelCenter = (
  purlin: ShapeObjectLine,
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number,
): ImmutableVector3 => {
  const { start, end } = purlin.shape
  const center = start.add(end).divideScalar(2)
  const z = Math.max(center.z, storeyTopZ ?? -Infinity) + FLOORPLAN_LABEL_Z_OFFSET
  return new ImmutableVector3(center.x, center.y, z)
}

/** 1 cm on paper in PDF points */
const PDF_POINTS_PER_CM = 72 / 2.54

/** head length of the PDF slab span arrow (0.25 cm) */
const PDF_SLAB_ARROW_HEAD_LENGTH_PT = 0.25 * PDF_POINTS_PER_CM

/**
 * Margin + border the slab writer adds to the text width when deciding
 * whether the label fits next to the arrow (`settings.margin`, `borderWidth`)
 */
const PDF_SLAB_LABEL_PADDING_PT = 1 + 1

/** the export draws the span arrow over 90 % of the span at the slab center */
const PDF_SLAB_ARROW_RELATIVE_LENGTH = 0.9

/** `isXSpan` tolerance of the V3 slab plan models */
const SPAN_AXIS_TOLERANCE = 1.0e-3

/**
 * Deckenposition / Dachposition label, mirroring the plan writer's
 * `add_bluebeam_annotations`: centered on the slab's point average, except for
 * slabs spanning along x whose span arrow is too short to frame the label —
 * those put the label's bottom edge one arrow head length above the center, so
 * it sits on top of the arrow instead of covering it.
 *
 * The label is lifted to the storey top: at the slab's mean height, the higher part of a sloped
 * roof slab (or another roof face, a dormer) would cover it and catch the click meant for it.
 */
export const getSlabLabelCenter = (
  slab: ShapeObject,
  textLines: string[],
  metrics: PlanLabelPdfMetrics,
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number,
): ImmutableVector3 => {
  const { points } = slab.shape
  const meanCenter = getCenterMultipleReduceMean(points)
  const center = new ImmutableVector3(
    meanCenter.x,
    meanCenter.y,
    Math.max(meanCenter.z, storeyTopZ ?? -Infinity) + FLOORPLAN_LABEL_Z_OFFSET,
  )
  // the export only knows four-point slabs (`spanLengthAtCenter`)
  if (points.length !== 4) return center

  const [p0, p1, p2, p3] = points
  // `span_direction`: the direction of the slab's second edge
  const spanDirection = p2.sub(p1).normalize()
  const isXSpan = Math.abs(spanDirection.y) < SPAN_AXIS_TOLERANCE
  if (!isXSpan) return center

  // `spanLengthAtCenter`: plan distance between the first and third edge centers
  const bottomCenter = p0.add(p1).divideScalar(2)
  const topCenter = p2.add(p3).divideScalar(2)
  const spanLengthAtCenter = Math.hypot(topCenter.x - bottomCenter.x, topCenter.y - bottomCenter.y)

  const arrowHeadLength = PDF_SLAB_ARROW_HEAD_LENGTH_PT * metrics.ptToWorld
  const boxWidth =
    getPdfTextBlockSizeWorld(textLines, metrics).width +
    2 * PDF_SLAB_LABEL_PADDING_PT * metrics.ptToWorld
  // The writer's arrow is capped at the box width + 2 cm as well, which is
  // always longer than box width + two arrow heads, so only the span decides.
  const arrowIsShorterThanLabel =
    spanLengthAtCenter * PDF_SLAB_ARROW_RELATIVE_LENGTH < boxWidth + 2 * arrowHeadLength
  if (!arrowIsShorterThanLabel) return center

  // bottom anchored in the PDF; our labels are center-anchored
  return center.add(
    new ImmutableVector3(
      0,
      arrowHeadLength + getPdfLabelBoxHeightWorld(metrics, true, textLines.length) / 2,
      0,
    ),
  )
}

/**
 * The column footprint is a small square crossed by its diagonals, so its
 * label goes above it (top of the plan) instead of on top of it.
 *
 * The floorplan draws the footprint around the column's upper end point,
 * spanning the cross section along the section axes (`Rips2DMesh` in the
 * toolkit): `width` along y, `height` along z of the coordinate system.
 * Looked up exactly like `FloorplanMesh` does, so the label stays glued to
 * the drawn box even where the cross section is missing.
 *
 * Mirrors `columnV2.planLabelPositionWorld` + the bottom-anchored PDF label:
 * footprint edge + gap + half the PDF box height.
 */
export const getColumnLabelCenter = (
  column: ShapeObjectLine,
  crossSection: CrossSection | undefined,
  metrics: PlanLabelPdfMetrics,
  numLabelLines = 1,
): ImmutableVector3 => {
  const halfWidth = (crossSection?.shape?.width ?? DEFAULT_COLUMN_CROSS_SECTION_SIZE) / 2
  const halfHeight = (crossSection?.shape?.height ?? DEFAULT_COLUMN_CROSS_SECTION_SIZE) / 2

  const { y_direction: sectionYAxis, z_direction: sectionZAxis } = column.coordinate_system

  // Half the footprint's extent along the plan's y axis, so a rotated
  // column clears its own corner rather than just its edge.
  const footprintHalfExtentY =
    Math.abs(sectionYAxis.normalize().y) * halfWidth +
    Math.abs(sectionZAxis.normalize().y) * halfHeight

  return column.shape.end.add(
    new ImmutableVector3(
      0,
      // the label is centered on its position, so half of the PDF box has to
      // clear the gap as well
      footprintHalfExtentY +
        PLAN_LABEL_GAP_WORLD +
        getPdfLabelBoxHeightWorld(metrics, true, numLabelLines) / 2,
      FLOORPLAN_LABEL_Z_OFFSET,
    ),
  )
}

/**
 * Top z of every storey as far as its labels are concerned: the storey's upper
 * boundary, raised to the highest point of the storey's slab positions where
 * those sit above it. Rip/lintel labels are placed at this level so they stack
 * above the slab labels of their storey.
 */
export const getStoreyTopZLookup = (model: PlanarModel): Record<string, number> => {
  const lookup: Record<string, number> = {}
  Object.entries(model.storey_boundaries).forEach(([storey, [, top]]) => {
    lookup[storey] = top
  })
  ;[...model.vertical_slabs, ...model.vertical_roof_slabs].forEach(slab => {
    const slabTop = Math.max(...slab.shape.points.map(point => point.z))
    if (!Number.isFinite(slabTop)) return
    lookup[slab.storey] = Math.max(lookup[slab.storey] ?? -Infinity, slabTop)
  })
  return lookup
}

interface PositionLabelPlacementParams {
  position: Position
  /** Whether the rip's wall runs along Y; `undefined` when the wall is unknown. */
  wallIsYCartesian: boolean | undefined
  crossSectionHeight: number | undefined
  metrics: PlanLabelPdfMetrics
  numLabelLines?: number
  /** Storey top (see `getStoreyTopZLookup`); the label is lifted to it when known. */
  storeyTopZ?: number
}

/**
 * Rip/lintel label placement mirroring the position-plan export
 * (`ripV2.planLabelPositionWorld` / `lintelV2.planLabelPositionWorld`): offset
 * from the element center to a fixed side, with half the PDF box height added
 * on top because these labels are center-anchored while the export anchors the
 * box's near edge.
 */
export const getPositionLabelPlacementWorld = ({
  position,
  wallIsYCartesian,
  crossSectionHeight,
  metrics,
  numLabelLines = 1,
  storeyTopZ,
}: PositionLabelPlacementParams): LabelPlacement | null => {
  const { element_type: type, start, end } = position

  const isRip = type === 'rips'
  // Rips follow their wall's axis, lintels their own (as in the export)
  const isYCartesian = isRip ? wallIsYCartesian : start.x === end.x
  if (isRip && isYCartesian === undefined) return null

  // At the storey top, above the slab labels (which sit at the slab level).
  const center = new ImmutableVector3(
    (start.x + end.x) / 2,
    (start.y + end.y) / 2,
    Math.max(start.z, end.z, storeyTopZ ?? -Infinity) +
      FLOORPLAN_LABEL_Z_OFFSET +
      POSITION_LABEL_Z_LIFT,
  )

  // Rips clear half their cross section (the export offsets from the
  // footprint edge), lintels start right at their axis line.
  const elementClearance = isRip ? (crossSectionHeight ?? DEFAULT_CROSS_SECTION_SIZE) / 2 : 0
  const offset =
    elementClearance +
    PLAN_LABEL_GAP_WORLD +
    getPdfLabelBoxHeightWorld(metrics, true, numLabelLines) / 2

  return getPositionLabelPlacement(center, !!isYCartesian, isRip ? -offset : offset)
}
